import { createId } from "@paralleldrive/cuid2";
import { CopySimpleIcon, PencilSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import type { SectionItem, SectionWithItem } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  ScrollArea,
} from "@reactive-resume/ui";
import { produce } from "immer";
import get from "lodash.get";
import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";

import type { DialogName } from "@/client/stores/dialog";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

type Props<T extends SectionItem> = {
  id: DialogName;
  form: UseFormReturn<T>;
  defaultValues: T;
  pendingKeyword?: string;
  disabled?: boolean;
  children: React.ReactNode;
};

export const SectionDialog = <T extends SectionItem>({
  id,
  form,
  defaultValues,
  pendingKeyword,
  disabled,
  children,
}: Props<T>) => {
  const { isOpen, mode, close, payload } = useDialog<T>(id);

  // Normalisation : `payload` peut être indéfini (dialogue ouvert sans item).
  const currentItem = payload?.item;

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) => {
    return get(state.resume.data.sections, id);
  }) as SectionWithItem<T> | null;

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen, payload]);

  const onSubmit = (values: T) => {
    if (!section) return;

    if (isCreate || isDuplicate) {
      const nextValues = {
        ...values,
        id: createId(),
      } as T;

      if (pendingKeyword && "keywords" in nextValues && Array.isArray(nextValues.keywords)) {
        nextValues.keywords = [...nextValues.keywords, pendingKeyword];
      }

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          draft.push(nextValues);
        }),
      );

      close();
      return;
    }

    if (isUpdate) {
      if (!currentItem?.id) return;

      const nextValues = { ...values } as T;

      if (pendingKeyword && "keywords" in nextValues && Array.isArray(nextValues.keywords)) {
        nextValues.keywords = [...nextValues.keywords, pendingKeyword];
      }

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === currentItem.id);
          if (index === -1) return;

          /*
           * Conserve les propriétés qui ne figurent pas forcément
           * dans le formulaire, notamment visible.
           */
          draft[index] = {
            ...draft[index],
            ...nextValues,
            visible: nextValues.visible ?? draft[index].visible ?? true,
          };
        }),
      );

      close();
      return;
    }

    if (isDelete) {
      if (!currentItem?.id) return;

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === currentItem.id);
          if (index === -1) return;

          draft.splice(index, 1);
        }),
      );

      close();
    }
  };

  const onReset = () => {
    if (isCreate) form.reset({ ...defaultValues, id: createId() } as T);
    if (isUpdate) form.reset({ ...defaultValues, ...currentItem });
    if (isDuplicate) form.reset({ ...currentItem, id: createId() } as T);
    if (isDelete) form.reset({ ...defaultValues, ...currentItem });
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent className="z-50">
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer cet élément ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action est irréversible. Vous pourrez annuler en cliquant sur le bouton
                  d'annulation dans la barre d'outils.
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="z-50 max-h-[95vh] max-w-[calc(100vw-2rem)] overflow-auto bg-surface text-text sm:max-w-3xl">
        <Form {...form}>
          <ScrollArea>
            <form
              className="max-h-[60vh] space-y-3 lg:max-h-fit"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <DialogHeader>
                <DialogTitle>
                  <div className="flex items-center space-x-2.5">
                    {isCreate && <PlusIcon />}
                    {isUpdate && <PencilSimpleIcon />}
                    {isDuplicate && <CopySimpleIcon />}
                    <h2>
                      {isCreate && "Ajouter un élément"}
                      {isUpdate && "Modifier l'élément"}
                      {isDuplicate && "Dupliquer l'élément"}
                    </h2>
                  </div>
                </DialogTitle>

                <VisuallyHidden>
                  <DialogDescription />
                </VisuallyHidden>
              </DialogHeader>

              {children}

              <DialogFooter>
                <Button
                  type="submit"
                  className="rounded-lg bg-focus text-white"
                  disabled={disabled}
                >
                  {isCreate && "Ajouter"}
                  {isUpdate && "Enregistrer"}
                  {isDuplicate && "Dupliquer"}
                </Button>
              </DialogFooter>
            </form>
          </ScrollArea>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
